import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '@/services/supabaseClient'
import { handleLogout } from '@/utils/authHelper'
import type { User } from '@supabase/supabase-js'
import EmsLogo from '@/components/EmsLogo'

interface Exam {
  id: number
  exam_name: string
  year: number
  is_live: boolean
  teacher_entry_enabled: boolean
  student_result_live?: boolean
  created_at: string
}

export default function ExamManagerPage() {
  const navigate = useNavigate()
  const [user, setUser] = useState<User | null>(null)
  const [exams, setExams] = useState<Exam[]>([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  
  // Create Modal state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newName, setNewName] = useState('')
  const [newYear, setNewYear] = useState(new Date().getFullYear())

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) { navigate('/login', { replace: true }); return }
      setUser(user)
      loadExams()
    })
  }, [navigate])

  async function loadExams() {
    setLoading(true)
    const { data, error } = await supabase
      .from('FMHS_exams_names')
      .select('*')
      .order('year', { ascending: false })
      .order('created_at', { ascending: false })
    
    if (error) {
      setStatus('Error: ' + error.message)
    } else {
      setExams(data || [])
    }
    setLoading(false)
  }

  const filteredExams = exams.filter(e => 
    e.exam_name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    e.year.toString().includes(searchTerm)
  )

  async function createExam(e: React.FormEvent) {
    e.preventDefault()
    if (!newName.trim()) return
    
    setStatus('Creating...')
    const { error } = await supabase.from('FMHS_exams_names').insert([
      { 
        exam_name: newName.trim(), 
        year: newYear
      }
    ])

    if (error) {
      setStatus('Error: ' + error.message)
      alert(error.message)
    } else {
      setStatus('Exam created successfully!')
      setNewName('')
      setShowCreateModal(false)
      loadExams()
    }
  }

  async function deleteExam(id: number, examName: string) {
    const { count } = await supabase
      .from('FMHS_exam_data')
      .select('*', { count: 'exact', head: true })
      .eq('exam_id', id)
    
    if (count && count > 0) {
      alert(`❌ ডিলিট করা যাচ্ছে না!\n\n"${examName}" পরীক্ষায় ${count} জন শিক্ষার্থীর ডাটা আছে।\nআগে সব ডাটা মুছে তারপর ডিলিট করুন।`)
      return
    }

    if (!confirm(`"${examName}" পরীক্ষাটি ডিলিট করতে চান?`)) return
    
    await supabase.from('FMHS_exam_subjects').delete().eq('exam_id', id)
    await supabase.from('FMHS_exam_teacher_selection').delete().eq('exam_id', id)
    
    const { error } = await supabase.from('FMHS_exams_names').delete().eq('id', id)
    if (error) {
      setStatus('Error: ' + error.message)
    } else {
      setStatus('Exam deleted!')
      loadExams()
    }
  }

  if (loading && !user) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  )

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'radial-gradient(circle at 18% 20%, rgba(37, 99, 235, 0.08), transparent 28%), radial-gradient(circle at 82% 12%, rgba(249, 115, 22, 0.08), transparent 30%), linear-gradient(180deg, #f9fafb, #f3f4f9)',
      color: '#0f172a',
      fontFamily: "'Sora', 'Outfit', system-ui, sans-serif"
    }}>
      {/* HEADER */}
      <header style={{
        position: 'sticky', top: '0.5rem', zIndex: 50,
        margin: '0.5rem 1.5rem 0',
        padding: '0.9rem 1.5rem',
        background: 'rgba(255, 255, 255, 0.94)',
        backdropFilter: 'blur(12px)',
        borderRadius: '20px',
        border: '1px solid rgba(226, 232, 240, 0.8)',
        boxShadow: '0 18px 50px rgba(15, 23, 42, 0.08)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <EmsLogo size={36} />
          <button 
            onClick={() => navigate('/dashboard')}
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              color: '#475569',
              padding: '0.45rem 1.1rem',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '13px',
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)'
            }}
          >
            ← College Dashboard
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Exam Command Center</span>
            </h1>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ fontSize: '13px', color: '#2563eb', fontWeight: 800, background: '#eff6ff', padding: '4px 12px', borderRadius: '999px', border: '1px solid #bfdbfe' }}>
            👤 {user?.email}
          </div>
          <button 
            onClick={() => handleLogout()}
            style={{
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: '#fff',
              border: 'none',
              padding: '0.45rem 1.1rem',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '13px',
              boxShadow: '0 12px 30px rgba(37, 99, 235, 0.25)'
            }}
          >Logout</button>
        </div>
      </header>

      {/* MAIN CONTAINER */}
      <main style={{ maxWidth: '1180px', margin: '0 auto', padding: '1.5rem' }}>
        
        {/* ACTIVE EXAM SESSIONS HEADER & ACTIONS */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '14px' }}>
          <div>
            <h2 style={{ margin: 0, color: '#0f172a', fontSize: '1.6rem', fontWeight: 900 }}>Existing Exams</h2>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13.5px' }}>Select an exam session to open its control panel.</p>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '14px' }}>🔍</span>
              <input 
                type="text" 
                placeholder="Search exam sessions..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ padding: '10px 16px 10px 36px', borderRadius: '12px', border: '1px solid #cbd5e1', width: '240px', fontSize: '13px', outline: 'none', background: '#fff' }}
              />
            </div>

            <button 
              onClick={() => setShowCreateModal(true)}
              style={{ 
                padding: '10px 22px', borderRadius: '12px', fontWeight: 800, 
                background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', 
                color: '#fff', border: 'none', cursor: 'pointer', 
                boxShadow: '0 8px 20px rgba(37, 99, 235, 0.25)', 
                fontSize: '13.5px', whiteSpace: 'nowrap' 
              }}
            >
              + Create New Examination
            </button>
          </div>
        </div>

        {status && (
          <div style={{ padding: '10px 16px', borderRadius: '10px', marginBottom: '20px', fontWeight: 700, fontSize: '13px', background: status.startsWith('Error') ? '#fee2e2' : '#d1fae5', color: status.startsWith('Error') ? '#991b1b' : '#065f46' }}>
            {status}
          </div>
        )}

        {/* EXAMS GRID */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}><div className="spinner" /></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
            {filteredExams.map(exam => (
              <div key={exam.id} style={{ 
                padding: '24px', borderRadius: '20px', background: '#fff', 
                border: '1.5px solid #e2e8f0', boxShadow: '0 6px 18px rgba(15, 23, 42, 0.05)',
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                transition: 'all 0.2s ease'
              }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 900, color: '#2563eb', background: '#eff6ff', padding: '3px 10px', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                      {exam.year} SESSION
                    </span>
                    <button 
                      onClick={() => deleteExam(exam.id, exam.exam_name)} 
                      style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px', fontSize: '16px' }}
                      title="Delete Exam"
                    >
                      🗑️
                    </button>
                  </div>
                  
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '1.35rem', fontWeight: 800, color: '#0f172a' }}>{exam.exam_name}</h4>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                    {/* Live Status (Static Display) */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: '10px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#475569' }}>Live Status</span>
                      <span style={{ 
                        padding: '4px 12px', 
                        borderRadius: '20px', 
                        fontSize: '12px', 
                        fontWeight: 800,
                        background: exam.is_live ? '#dcfce7' : '#fee2e2',
                        color: exam.is_live ? '#15803d' : '#b91c1c'
                      }}>
                        {exam.is_live ? '● Live' : '○ Offline'}
                      </span>
                    </div>
                    
                    {/* Teacher Entry (Static Display) */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: '10px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#475569' }}>Teacher Entry</span>
                      <span style={{ 
                        padding: '4px 12px', 
                        borderRadius: '20px', 
                        fontSize: '12px', 
                        fontWeight: 800,
                        background: exam.teacher_entry_enabled ? '#dcfce7' : '#fee2e2',
                        color: exam.teacher_entry_enabled ? '#15803d' : '#b91c1c'
                      }}>
                        {exam.teacher_entry_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>

                    {/* Student Access (Static Display) */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: '10px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#475569' }}>Student Access</span>
                      <span style={{ 
                        padding: '4px 12px', 
                        borderRadius: '20px', 
                        fontSize: '12px', 
                        fontWeight: 800,
                        background: exam.student_result_live ? '#dcfce7' : '#f1f5f9',
                        color: exam.student_result_live ? '#15803d' : '#64748b'
                      }}>
                        {exam.student_result_live ? 'Published' : 'Hidden'}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <Link 
                    to={`/exam-panel/${exam.id}`} 
                    style={{ display: 'block', textAlign: 'center', textDecoration: 'none', borderRadius: '12px', background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', padding: '11px', fontWeight: 800, fontSize: '14px', boxShadow: '0 8px 18px rgba(37, 99, 235, 0.2)' }}
                  >
                    Open Exam Panel →
                  </Link>
                </div>
              </div>
            ))}
            {filteredExams.length === 0 && (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: '#64748b', background: '#fff', borderRadius: '16px', border: '2px dashed #cbd5e1' }}>
                No exams found. Click "+ Create New Examination" above to create one!
              </div>
            )}
          </div>
        )}
      </main>

      {/* CREATE NEW EXAM MODAL */}
      {showCreateModal && (
        <div style={{ 
          position: 'fixed', inset: 0, 
          background: 'rgba(15, 23, 42, 0.5)', 
          backdropFilter: 'blur(4px)', 
          display: 'flex', alignItems: 'center', justifyContent: 'center', 
          zIndex: 1000 
        }}>
          <div style={{ 
            background: '#fff', padding: '32px', borderRadius: '24px', 
            width: '100%', maxWidth: '440px', 
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h2 style={{ fontSize: '1.6rem', fontWeight: 900, margin: 0, color: '#0f172a' }}>New Exam Session</h2>
              <button 
                onClick={() => setShowCreateModal(false)} 
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}
              >&times;</button>
            </div>
            <p style={{ color: '#64748b', fontSize: '13.5px', marginBottom: '24px' }}>Create a fresh exam session for the selected academic year.</p>
            
            <form onSubmit={createExam}>
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>EXAM NAME</label>
                <input 
                  type="text" 
                  style={{ width: '100%', borderRadius: '12px', padding: '12px 14px', border: '1px solid #cbd5e1', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', fontSize: '14px' }} 
                  value={newName} 
                  onChange={e => setNewName(e.target.value)} 
                  placeholder="e.g. Half Yearly Examination 2026" 
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '28px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>ACADEMIC YEAR</label>
                <select 
                  style={{ width: '100%', borderRadius: '12px', padding: '12px 14px', border: '1px solid #cbd5e1', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', fontSize: '14px' }} 
                  value={newYear} 
                  onChange={e => setNewYear(Number(e.target.value))}
                >
                  {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  type="submit" 
                  style={{ flex: 2, padding: '12px', borderRadius: '12px', background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', border: 'none', fontWeight: 800, color: '#fff', cursor: 'pointer', fontSize: '13.5px' }}
                >INITIALIZE SESSION</button>
                <button 
                  type="button" 
                  onClick={() => setShowCreateModal(false)}
                  style={{ flex: 1, padding: '12px', borderRadius: '12px', background: '#f1f5f9', border: 'none', fontWeight: 700, color: '#475569', cursor: 'pointer', fontSize: '13.5px' }}
                >CANCEL</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <footer style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', fontSize: '13px', borderTop: '1px solid #e2e8f0', marginTop: '40px' }}>
        &copy; 2026 Feni South-East College. All rights reserved. FMHS Result & Exam Control Center
      </footer>
    </div>
  )
}
